/**
 * AuthContext — stores JWT token + current user and exposes login/logout.
 *
 * On mount it checks for an existing token and validates it by calling
 * GET /api/auth/me.  If the token is invalid or expired it clears state and
 * redirects to the login page.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api } from '../api/client.ts';
import type { User } from '../types.ts';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /** Persist token and fetch user profile. */
  const login = useCallback(async (token: string) => {
    api.auth.handleAuthCallback(token);
    try {
      const res = await api.auth.getMe();
      if (res.success && res.data) {
        setUser(res.data.user);
      }
    } catch {
      api.auth.logout();
      setUser(null);
    }
  }, []);

  /** Clear everything. */
  const logout = useCallback(() => {
    api.auth.logout();
    setUser(null);
  }, []);

  /** On mount — revalidate any stored token. */
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!api.auth.hasToken()) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await api.auth.getMe();
        if (!cancelled && res.success && res.data) {
          setUser(res.data.user);
        }
      } catch {
        if (!cancelled) {
          api.auth.logout();
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      login,
      logout,
    }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
